import React from 'react';
import { Link } from 'react-router-dom';
import type { WizardPhase } from './wizardState';

type Props = {
  phase: WizardPhase;
  createdSearchId: string | null;
  duplicateSubscriptionId: string | null;
  errorMessage: string | null;
  onBack: () => void;
  onNext: () => void;
  onStartOver: () => void;
  onCreateAnother: () => void;
  onRetry: () => void;
  onChooseRecipient: () => void;
  onBackToSearch: () => void;
};

export default function WizardFooter({
  phase,
  createdSearchId,
  duplicateSubscriptionId,
  errorMessage,
  onBack,
  onNext,
  onStartOver,
  onCreateAnother,
  onRetry,
  onChooseRecipient,
  onBackToSearch,
}: Props) {
  const locked = phase === 'creating' || phase === 'success';

  if (phase === 'success' && createdSearchId) {
    return (
      <div className="wizard-footer wizard-footer--success">
        <p className="wizard-footer-success-label" role="status">
          Alert created successfully — choose your next step
        </p>
        <div className="wizard-footer-success-actions">
          <Link to={`/searches/${createdSearchId}/links`} className="btn btn-ghost wizard-btn-secondary">
            View search
          </Link>
          <button type="button" className="btn btn-primary wizard-cta" onClick={onCreateAnother}>
            Create another
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'duplicate') {
    return (
      <div className="wizard-footer wizard-footer--duplicate">
        <p className="wizard-footer-error-label" role="alert">
          {errorMessage || 'This alert already exists for this recipient and search.'}
        </p>
        <div className="wizard-footer-end">
          {duplicateSubscriptionId ? (
            <Link
              to="/subscriptions"
              className="btn btn-ghost wizard-btn-secondary"
              state={{ highlightSubscriptionId: duplicateSubscriptionId }}
            >
              View existing alert
            </Link>
          ) : (
            <Link to="/subscriptions" className="btn btn-ghost wizard-btn-secondary">
              View existing alert
            </Link>
          )}
          <button type="button" className="btn btn-ghost wizard-btn-secondary" onClick={onChooseRecipient}>
            Choose another recipient
          </button>
          <button type="button" className="btn btn-ghost wizard-btn-secondary" onClick={onBackToSearch}>
            Back to search
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="wizard-footer wizard-footer--error">
        <p className="wizard-footer-error-label" role="alert">
          {errorMessage || 'Setup failed. Please try again.'}
        </p>
        <div className="wizard-footer-end">
          <button type="button" className="btn btn-ghost wizard-btn-secondary" onClick={onBack}>
            ← Back to review
          </button>
          <button type="button" className="btn btn-primary wizard-cta" onClick={onRetry}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'creating') {
    return (
      <div className="wizard-footer wizard-footer--creating">
        <p className="muted wizard-footer-creating-label">Please wait while your alert is created…</p>
      </div>
    );
  }

  const showBack = phase !== 'category';

  return (
    <div className="wizard-footer">
      <div className="wizard-footer-start">
        {showBack && (
          <button
            type="button"
            className="btn btn-ghost wizard-btn-secondary"
            onClick={onBack}
            disabled={locked}
          >
            ← Back
          </button>
        )}
        <button
          type="button"
          className="wizard-text-action"
          onClick={onStartOver}
          disabled={locked}
        >
          Start over
        </button>
      </div>
      <div className="wizard-footer-end">
        {phase === 'review' ? (
          <button type="submit" className="btn btn-primary wizard-cta">
            Create alert
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary wizard-cta"
            onClick={onNext}
            disabled={locked}
          >
            Continue →
          </button>
        )}
      </div>
    </div>
  );
}
